import { useMemo } from 'react';
import { format, parseISO, differenceInCalendarDays, isAfter } from 'date-fns';
import { useAppStore } from '../../stores/appStore';

export function UpcomingWidget() {
  const events = useAppStore((s) => s.events);
  const calendars = useAppStore((s) => s.calendars);
  const openDay = useAppStore((s) => s.openDay);

  const upcoming = useMemo(() => {
    const now = new Date();
    return [...events]
      .filter((e) => isAfter(parseISO(e.end_at), now) || e.all_day)
      .filter((e) => {
        // For all-day, include today and future
        if (e.all_day) {
          return differenceInCalendarDays(parseISO(e.start_at), now) >= 0;
        }
        return isAfter(parseISO(e.start_at), new Date(now.getTime() - 3600e3));
      })
      .sort(
        (a, b) =>
          parseISO(a.start_at).getTime() - parseISO(b.start_at).getTime()
      )
      .slice(0, 8);
  }, [events]);

  const color = (id: string) =>
    calendars.find((c) => c.id === id)?.color || '#6366F1';

  return (
    <div className="flex h-full flex-col">
      <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-400">
        Upcoming
      </h3>
      <ul className="flex-1 space-y-1.5 overflow-auto">
        {upcoming.map((e) => {
          const start = parseISO(e.start_at);
          const days = differenceInCalendarDays(start, new Date());
          const countdown =
            days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `In ${days} days`;
          return (
            <li key={e.id}>
              <button
                type="button"
                onClick={() => openDay(e.start_at.slice(0, 10))}
                className="flex w-full items-center gap-2 rounded-lg px-1 py-1 text-left hover:bg-slate-800/50 light:hover:bg-slate-100"
              >
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ background: color(e.calendar_id) }}
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{e.title}</div>
                  <div className="text-xs text-slate-500">
                    {e.all_day
                      ? format(start, 'MMM d')
                      : format(start, 'MMM d · h:mm a')}
                  </div>
                </div>
                <span className="shrink-0 rounded-full bg-violet-500/15 px-2 py-0.5 text-xs font-semibold text-violet-300">
                  {countdown}
                </span>
              </button>
            </li>
          );
        })}
        {upcoming.length === 0 && (
          <li className="text-sm text-slate-500">No upcoming events</li>
        )}
      </ul>
    </div>
  );
}
